'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { RefreshCw, Package, AlertTriangle, Clock, Sliders, ShoppingCart, Plus, Minus, Trash2, CheckCircle } from 'lucide-react';

// ─── Kit data (same pool as my-kits) ───────────────────────────────────────
interface Kit {
  id: string;
  serialNo: string;
  ccNo: string;
  kitName: string;
  expiry: string;
  ccName: string;
  status: 'active' | 'near-3m' | 'near-6m' | 'expired' | 'short';
}

const KIT_NAMES = [
  'BLOOD GROUPING KIT', 'MALARIA TEST KIT', 'PREGNANCY TEST KIT', 'DENGUE TEST KIT',
  'TROPONIN TEST KIT', 'TYPHOID TEST KIT', 'HIV TEST KIT', 'CHOLERA TEST KIT',
  'HEPATITIS B KIT', 'LEPTOSPIROSIS KIT', 'SCRUB TYPHUS KIT', 'CHIKUNGUNYA KIT',
  'URINE STRIP KIT', 'STOOL TEST KIT', 'GLUCOSE TEST KIT', 'SEARCH LIGHT KIT',
  'WOUND CARE KIT', 'SURGICAL DRESSING KIT', 'IV CANNULA KIT', 'OXYGEN MASK KIT',
  'SUTURE KIT', 'SPLINT KIT', 'BURN CARE KIT', 'TRAUMA CARE KIT',
  'SNAKE BITE KIT', 'ANTIDOTE KIT', 'RESUSCITATION KIT', 'DEFIBRILLATOR PAD KIT',
  'BLOOD PRESSURE KIT', 'PULSE OXIMETER KIT',
];

const CC_NAMES = [
  'LAB CARD TEST SET', 'RAPID DIAGNOSTIC SET', 'VECTOR DISEASE SET',
  'GENERAL DIAGNOSTIC SET', 'METABOLIC PANEL SET', 'EMERGENCY CAMP LIGHT SET',
  'SURGICAL INSTRUMENT SET', 'TRAUMA RESPONSE SET', 'CRITICAL CARE SET',
  'FIELD MEDICAL SET',
];

function generateKits(): Kit[] {
  const depts = ['TECH', 'MED', 'SURG', 'LAB', 'ICU'];
  const statusPool: Kit['status'][] = [];
  for (let i = 0; i < 721; i++) statusPool.push('active');
  for (let i = 0; i < 3; i++) statusPool.push('near-3m');
  for (let i = 0; i < 17; i++) statusPool.push('near-6m');
  for (let i = 0; i < 149; i++) statusPool.push('expired');
  for (let i = 0; i < 69; i++) statusPool.push('short');
  while (statusPool.length < 1089) statusPool.push('active');
  for (let i = statusPool.length - 1; i > 0; i--) {
    const j = (i * 1103515245 + 12345) % statusPool.length;
    [statusPool[i], statusPool[j]] = [statusPool[j], statusPool[i]];
  }
  const today = new Date('2026-07-01');
  return Array.from({ length: 1089 }, (_, idx) => {
    let i = idx + 1;
    const status = statusPool[idx];
    const dept = depts[i % depts.length];
    const num = String(Math.floor(i / 10) + 1).padStart(2, '0');
    let expiry = '';
    if (status === 'short') {
      expiry = 'NA';
    } else if (status === 'expired') {
      const d = new Date(today); d.setDate(d.getDate() - ((i % 365) + 1));
      expiry = d.toISOString().split('T')[0];
    } else if (status === 'near-3m') {
      const d = new Date(today); d.setDate(d.getDate() + ((i % 89) + 1));
      expiry = d.toISOString().split('T')[0];
    } else if (status === 'near-6m') {
      const d = new Date(today); d.setDate(d.getDate() + 91 + ((i % 89) + 1));
      expiry = d.toISOString().split('T')[0];
    } else {
      const d = new Date(today); d.setDate(d.getDate() + 181 + ((i % 365) + 1));
      expiry = d.toISOString().split('T')[0];
    }
    return {
      id: `ckit-${String(i).padStart(4, '0')}`,
      serialNo: `BHISHM/AIIMS/${dept}/PTN/${num}`,
      ccNo: `${(i % 30) + 1}/25`,
      kitName: KIT_NAMES[i % KIT_NAMES.length],
      expiry,
      ccName: CC_NAMES[i % CC_NAMES.length],
      status,
    };
  });
}

const ALL_KITS = generateKits();

function formatExpiry(expiry: string) {
  if (expiry === 'NA') return 'NA';
  const p = expiry.split('-');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : expiry;
}

// ─── Types ──────────────────────────────────────────────────────────────────
type ReplenishTab = 'fresh' | 'partial' | 'custom';
type PartialFilter = 'expired' | 'short' | 'near-3m' | 'near-6m';

interface CartItem {
  kitName: string;
  qty: number;
}

// ─── Unique kit names for custom selection ──────────────────────────────────
const UNIQUE_KIT_NAMES = Array.from(new Set(KIT_NAMES));

// ─── Component ──────────────────────────────────────────────────────────────
export default function ReplenishmentPage() {
  const [activeTab, setActiveTab] = useState<ReplenishTab>('fresh');
  const [submitted, setSubmitted] = useState(false);

  // Fresh replenishment state
  const [freshQty, setFreshQty] = useState<Record<string, number>>({});

  // Partial replenishment state
  const [partialFilter, setPartialFilter] = useState<PartialFilter>('expired');
  const [partialSelected, setPartialSelected] = useState<Set<string>>(new Set());

  // Custom replenishment state
  const [customCart, setCustomCart] = useState<CartItem[]>([]);
  const [customSearch, setCustomSearch] = useState('');
  const [customQtyInput, setCustomQtyInput] = useState<Record<string, string>>({});

  // ── Fresh: unique kit names with qty input
  const freshKitNames = UNIQUE_KIT_NAMES;

  function handleFreshQty(name: string, delta: number) {
    setFreshQty(prev => {
      const cur = prev[name] ?? 0;
      const next = Math.max(0, cur + delta);
      return { ...prev, [name]: next };
    });
  }

  const freshTotal = Object.values(freshQty).reduce((a, b) => a + b, 0);

  // ── Partial: filter kits
  const partialKits = useMemo(() => {
    return ALL_KITS.filter(k => k.status === partialFilter);
  }, [partialFilter]);

  function togglePartialKit(id: string) {
    setPartialSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllPartial() {
    setPartialSelected(new Set(partialKits.map(k => k.id)));
  }

  function clearPartialSelection() {
    setPartialSelected(new Set());
  }

  // ── Custom: search + add to cart
  const filteredCustomKits = useMemo(() => {
    const q = customSearch.trim().toLowerCase();
    if (!q) return UNIQUE_KIT_NAMES;
    return UNIQUE_KIT_NAMES.filter(n => n.toLowerCase().includes(q));
  }, [customSearch]);

  function addToCart(name: string) {
    const qty = parseInt(customQtyInput[name] ?? '1', 10);
    if (!qty || qty < 1) return;
    setCustomCart(prev => {
      const existing = prev.find(c => c.kitName === name);
      if (existing) {
        return prev.map(c => c.kitName === name ? { ...c, qty: c.qty + qty } : c);
      }
      return [...prev, { kitName: name, qty }];
    });
    setCustomQtyInput(prev => ({ ...prev, [name]: '' }));
  }

  function removeFromCart(name: string) {
    setCustomCart(prev => prev.filter(c => c.kitName !== name));
  }

  function updateCartQty(name: string, qty: number) {
    if (qty < 1) return;
    setCustomCart(prev => prev.map(c => c.kitName === name ? { ...c, qty } : c));
  }

  // ── Submit
  function handleSubmit() {
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
  }

  const TABS: { key: ReplenishTab; label: string; icon: React.ReactNode; desc: string; color: string }[] = [
    {
      key: 'fresh',
      label: 'Fresh Replenishment',
      icon: <RefreshCw size={15} />,
      desc: 'Order brand-new kits to expand your inventory',
      color: '#0369a1',
    },
    {
      key: 'partial',
      label: 'Partial Replenishment',
      icon: <AlertTriangle size={15} />,
      desc: 'Replace expired, short, or near-expiry kits',
      color: '#b45309',
    },
    {
      key: 'custom',
      label: 'Custom Replenishment',
      icon: <Sliders size={15} />,
      desc: 'Manually select kits and specify quantities',
      color: '#7c3aed',
    },
  ];

  const PARTIAL_FILTERS: { key: PartialFilter; label: string; count: number; color: string; bg: string; border: string }[] = [
    { key: 'expired', label: 'Expired Kits', count: ALL_KITS.filter(k => k.status === 'expired').length, color: '#dc2626', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.25)' },
    { key: 'short', label: 'Kits Short', count: ALL_KITS.filter(k => k.status === 'short').length, color: '#ea580c', bg: 'rgba(234,88,12,0.08)', border: 'rgba(234,88,12,0.25)' },
    { key: 'near-3m', label: 'Expiring in 3 Months', count: ALL_KITS.filter(k => k.status === 'near-3m').length, color: '#b45309', bg: 'rgba(180,83,9,0.08)', border: 'rgba(180,83,9,0.25)' },
    { key: 'near-6m', label: 'Expiring in 6 Months', count: ALL_KITS.filter(k => k.status === 'near-6m').length, color: '#d97706', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.25)' },
  ];

  const activeTabMeta = TABS.find(t => t.key === activeTab)!;

  return (
    <AppLayout activePath="/replenishment">
      <div className="px-4 lg:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-700" style={{ color: '#0f2744' }}>Replenishment</h1>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              Place replenishment orders for your kit inventory — AIIMS PATNA
            </p>
          </div>
        </div>

        {/* Success toast */}
        {submitted && (
          <div
            className="flex items-start gap-3 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)' }}
          >
            <CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p className="text-sm font-600" style={{ color: '#16a34a' }}>
                {activeTab === 'partial'
                  ? `Partial replenishment order submitted — ${partialSelected.size} kit${partialSelected.size !== 1 ? 's' : ''} grouped into a single order.`
                  : activeTab === 'fresh'
                  ? `Fresh replenishment order submitted successfully! Our team will review and process your request.`
                  : `Custom replenishment order submitted successfully! Our team will review and process your request.`}
              </p>
              {activeTab === 'partial' && partialSelected.size > 0 && (
                <p className="text-xs mt-1" style={{ color: '#15803d' }}>
                  All selected kits are tracked under a single order reference in My Orders.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tab selector */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-start gap-3 p-4 rounded-xl text-left transition-all"
                style={isActive
                  ? { background: `rgba(${tab.key === 'fresh' ? '3,105,161' : tab.key === 'partial' ? '180,83,9' : '124,58,237'},0.08)`, border: `1.5px solid ${tab.color}` }
                  : { background: '#ffffff', border: '1.5px solid #e2e8f0' }
                }
              >
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: isActive ? tab.color : '#f1f5f9', color: isActive ? '#fff' : '#94a3b8' }}
                >
                  {tab.icon}
                </span>
                <div>
                  <p className="text-sm font-700 leading-snug" style={{ color: isActive ? tab.color : '#1a2332' }}>{tab.label}</p>
                  <p className="text-[11px] mt-0.5 leading-snug" style={{ color: '#64748b' }}>{tab.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── FRESH REPLENISHMENT ── */}
        {activeTab === 'fresh' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-600" style={{ color: '#0f2744' }}>
                Select kits and specify quantities for fresh order
              </p>
              {freshTotal > 0 && (
                <span className="text-xs font-600 px-3 py-1 rounded-full" style={{ background: 'rgba(3,105,161,0.1)', color: '#0369a1' }}>
                  {freshTotal} kits selected
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {freshKitNames.map(name => {
                const qty = freshQty[name] ?? 0;
                return (
                  <div
                    key={name}
                    className="rounded-xl p-4 flex items-center justify-between gap-3"
                    style={{
                      background: qty > 0 ? 'rgba(3,105,161,0.04)' : '#ffffff',
                      border: qty > 0 ? '1.5px solid rgba(3,105,161,0.3)' : '1px solid #e2e8f0',
                    }}
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: qty > 0 ? 'rgba(3,105,161,0.12)' : '#f1f5f9' }}
                      >
                        <Package size={13} style={{ color: qty > 0 ? '#0369a1' : '#94a3b8' }} />
                      </div>
                      <p className="text-xs font-600 leading-snug truncate" style={{ color: '#0f2744' }}>{name}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleFreshQty(name, -1)}
                        disabled={qty === 0}
                        className="w-6 h-6 rounded-md flex items-center justify-center transition-colors disabled:opacity-30"
                        style={{ background: '#f1f5f9', color: '#64748b' }}
                      >
                        <Minus size={11} />
                      </button>
                      <span className="w-8 text-center text-sm font-700" style={{ color: '#0f2744' }}>{qty}</span>
                      <button
                        onClick={() => handleFreshQty(name, 1)}
                        className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
                        style={{ background: '#0369a1', color: '#fff' }}
                      >
                        <Plus size={11} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {freshTotal > 0 && (
              <div className="flex justify-end pt-2">
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 transition-all"
                  style={{ background: '#0369a1', color: '#fff' }}
                >
                  <ShoppingCart size={15} />
                  Submit Fresh Order ({freshTotal} kits)
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── PARTIAL REPLENISHMENT ── */}
        {activeTab === 'partial' && (
          <div className="space-y-4">
            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
              {PARTIAL_FILTERS.map(f => {
                const isActive = partialFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => { setPartialFilter(f.key); setPartialSelected(new Set()); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 transition-all"
                    style={isActive
                      ? { background: f.color, color: '#fff', border: `1px solid ${f.color}` }
                      : { background: f.bg, color: f.color, border: `1px solid ${f.border}` }
                    }
                  >
                    <Clock size={11} />
                    {f.label}
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-700"
                      style={isActive ? { background: 'rgba(255,255,255,0.25)', color: '#fff' } : { background: 'rgba(255,255,255,0.7)', color: f.color }}
                    >
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Select all / clear */}
            <div className="flex items-center justify-between">
              <p className="text-xs font-500" style={{ color: '#64748b' }}>
                {partialSelected.size > 0
                  ? <><span className="font-700" style={{ color: '#0369a1' }}>{partialSelected.size}</span> kits selected for replenishment</>
                  : `${partialKits.length} kits in this category`
                }
              </p>
              <div className="flex gap-2">
                <button
                  onClick={selectAllPartial}
                  className="text-xs font-600 px-3 py-1 rounded-lg transition-colors"
                  style={{ background: 'rgba(3,105,161,0.08)', color: '#0369a1' }}
                >
                  Select All
                </button>
                {partialSelected.size > 0 && (
                  <button
                    onClick={clearPartialSelection}
                    className="text-xs font-600 px-3 py-1 rounded-lg transition-colors"
                    style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Kit list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {partialKits.map(kit => {
                const isSelected = partialSelected.has(kit.id);
                const activeFilter = PARTIAL_FILTERS.find(f => f.key === partialFilter)!;
                return (
                  <button
                    key={kit.id}
                    onClick={() => togglePartialKit(kit.id)}
                    className="rounded-xl p-4 text-left transition-all space-y-2"
                    style={isSelected
                      ? { background: `rgba(${partialFilter === 'expired' ? '220,38,38' : partialFilter === 'short' ? '234,88,12' : partialFilter === 'near-3m' ? '180,83,9' : '217,119,6'},0.07)`, border: `1.5px solid ${activeFilter.color}` }
                      : { background: '#ffffff', border: '1px solid #e2e8f0' }
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 flex-1 min-w-0">
                        <p className="text-[10px]" style={{ color: '#64748b' }}>Serial: {kit.serialNo}</p>
                        <p className="text-[10px]" style={{ color: '#64748b' }}>CC No: {kit.ccNo}</p>
                      </div>
                      <div
                        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={isSelected
                          ? { background: activeFilter.color, border: `1.5px solid ${activeFilter.color}` }
                          : { background: '#fff', border: '1.5px solid #cbd5e1' }
                        }
                      >
                        {isSelected && (
                          <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <p className="text-xs font-700 leading-snug" style={{ color: '#0f2744' }}>{kit.kitName}</p>
                    <p className="text-[11px] font-500" style={{ color: activeFilter.color }}>
                      {kit.status === 'short' ? 'Stock Short' : `Expiry: ${formatExpiry(kit.expiry)}`}
                    </p>
                  </button>
                );
              })}
            </div>

            {partialSelected.size > 0 && (
              <div className="flex justify-end pt-2">
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 transition-all"
                  style={{ background: '#b45309', color: '#fff' }}
                >
                  <ShoppingCart size={15} />
                  Submit Partial Order ({partialSelected.size} kits)
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── CUSTOM REPLENISHMENT ── */}
        {activeTab === 'custom' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: kit selector */}
              <div className="space-y-3">
                <p className="text-sm font-600" style={{ color: '#0f2744' }}>Select Kits</p>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search kit name…"
                    value={customSearch}
                    onChange={e => setCustomSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-xs rounded-xl outline-none"
                    style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#1a2332' }}
                  />
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredCustomKits.map(name => {
                    const inCart = customCart.find(c => c.kitName === name);
                    return (
                      <div
                        key={name}
                        className="flex items-center gap-3 p-3 rounded-xl"
                        style={{
                          background: inCart ? 'rgba(124,58,237,0.04)' : '#ffffff',
                          border: inCart ? '1.5px solid rgba(124,58,237,0.3)' : '1px solid #e2e8f0',
                        }}
                      >
                        <Package size={13} style={{ color: inCart ? '#7c3aed' : '#94a3b8', flexShrink: 0 }} />
                        <p className="text-xs font-600 flex-1 leading-snug" style={{ color: '#0f2744' }}>{name}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <input
                            type="number"
                            min={1}
                            placeholder="Qty"
                            value={customQtyInput[name] ?? ''}
                            onChange={e => setCustomQtyInput(prev => ({ ...prev, [name]: e.target.value }))}
                            className="w-14 text-xs text-center rounded-lg px-2 py-1 outline-none"
                            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1a2332' }}
                          />
                          <button
                            onClick={() => addToCart(name)}
                            className="px-2.5 py-1 rounded-lg text-xs font-600 transition-colors"
                            style={{ background: '#7c3aed', color: '#fff' }}
                          >
                            {inCart ? 'Add More' : 'Add'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {filteredCustomKits.length === 0 && (
                    <p className="text-xs text-center py-8" style={{ color: '#94a3b8' }}>No kits match your search</p>
                  )}
                </div>
              </div>

              {/* Right: cart */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-600" style={{ color: '#0f2744' }}>Order Cart</p>
                  {customCart.length > 0 && (
                    <span className="text-xs font-600 px-2.5 py-1 rounded-full" style={{ background: 'rgba(124,58,237,0.1)', color: '#7c3aed' }}>
                      {customCart.length} kit type{customCart.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {customCart.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center py-16 rounded-xl"
                    style={{ border: '1px dashed #e2e8f0', background: '#f8fafc' }}
                  >
                    <ShoppingCart size={28} style={{ color: '#cbd5e1', marginBottom: '8px' }} />
                    <p className="text-sm font-600" style={{ color: '#94a3b8' }}>Cart is empty</p>
                    <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Add kits from the left panel</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customCart.map(item => (
                      <div
                        key={item.kitName}
                        className="flex items-center gap-3 p-3 rounded-xl"
                        style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}
                      >
                        <Package size={13} style={{ color: '#7c3aed', flexShrink: 0 }} />
                        <p className="text-xs font-600 flex-1 leading-snug truncate" style={{ color: '#0f2744' }}>{item.kitName}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => updateCartQty(item.kitName, item.qty - 1)}
                            disabled={item.qty <= 1}
                            className="w-6 h-6 rounded-md flex items-center justify-center disabled:opacity-30"
                            style={{ background: '#f1f5f9', color: '#64748b' }}
                          >
                            <Minus size={10} />
                          </button>
                          <span className="w-8 text-center text-sm font-700" style={{ color: '#0f2744' }}>{item.qty}</span>
                          <button
                            onClick={() => updateCartQty(item.kitName, item.qty + 1)}
                            className="w-6 h-6 rounded-md flex items-center justify-center"
                            style={{ background: '#7c3aed', color: '#fff' }}
                          >
                            <Plus size={10} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.kitName)}
                            className="w-6 h-6 rounded-md flex items-center justify-center ml-1"
                            style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div
                      className="flex items-center justify-between px-3 py-2 rounded-xl mt-2"
                      style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)' }}
                    >
                      <p className="text-xs font-600" style={{ color: '#7c3aed' }}>Total Kits</p>
                      <p className="text-sm font-700" style={{ color: '#7c3aed' }}>
                        {customCart.reduce((a, c) => a + c.qty, 0)}
                      </p>
                    </div>
                    <button
                      onClick={handleSubmit}
                      className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 transition-all mt-2"
                      style={{ background: '#7c3aed', color: '#fff' }}
                    >
                      <ShoppingCart size={15} />
                      Submit Custom Order
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
